import { updateWorkCategorySelectionSchema, workCategoriesSchema } from '@houzy/schemas/partner-cockpit/company';
import {
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';
import { STALE_TIME_MS } from '../../../lib/queryClient';
import type { WorkCategory } from '../types';

export async function fetchWorkCategories(signal?: AbortSignal): Promise<WorkCategory[]> {
    return apiRequest(partnerCockpitApiPath('company/work-categories/'), {
        signal,
        responseSchema: workCategoriesSchema,
    });
}

export const workCategoriesQueryOptions = queryOptions({
    queryKey: ['settings', 'workCategories'] as const,
    queryFn: ({ signal }) => fetchWorkCategories(signal),
    staleTime: STALE_TIME_MS,
});

export function useWorkCategories(): UseQueryResult<WorkCategory[]> {
    return useQuery(workCategoriesQueryOptions);
}

async function fetchWorkCategorySelection(signal?: AbortSignal): Promise<ReadonlySet<string>> {
    const { keys } = await apiRequest(partnerCockpitApiPath('company/work-categories/work-category-selection'), {
        signal,
        responseSchema: updateWorkCategorySelectionSchema,
    });
    return new Set(keys);
}

export const workCategorySelectionQueryKey = ['settings', 'workCategorySelection'] as const;

export const workCategorySelectionQueryOptions = queryOptions({
    queryKey: workCategorySelectionQueryKey,
    queryFn: ({ signal }) => fetchWorkCategorySelection(signal),
    staleTime: STALE_TIME_MS,
});

export function useWorkCategorySelection(): UseQueryResult<ReadonlySet<string>> {
    return useQuery(workCategorySelectionQueryOptions);
}

export function useSaveWorkCategorySelection(): UseMutationResult<void, Error, ReadonlySet<string>> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['settings', 'save', 'workCategorySelection'],
        mutationFn: (keys) =>
            apiRequest(partnerCockpitApiPath('company/work-categories/work-category-selection'), {
                method: 'PUT',
                body: { keys: Array.from(keys) },
            }),
        onSuccess: (_, keys) => {
            queryClient.setQueryData<ReadonlySet<string>>(workCategorySelectionQueryKey, keys);
        },
    });
}
